import { FC, useEffect } from 'react';
import { useField } from 'informed';
import { CloseIcon, DocUploadIcon } from '@assets/svg';
import { InformedFieldState } from '@common/interfaces';
import css from './styles.module.scss';

interface IUploadField {
  name: string;
  maxSize?: number;
  maxCount?: number;
  label?: string;
  accept?: string;
  withPreview?: boolean;
}

export interface PseudoFile {
  url: string;
  name: string;
  size: number;
  type: string;
  __existing: boolean;
}

const UploadField: FC<IUploadField> = ({
  accept,
  name,
  maxSize,
  maxCount,
  label = 'Select file',
  withPreview = false,
}) => {
  const { fieldState, fieldApi, render, ref } = useField({ name });

  const { value: fieldValue = [], error, showError } = fieldState as InformedFieldState<File[]>;

  const { setValue, setError } = fieldApi;

  const maxSizeValue = maxSize ? 1048576 * maxSize : undefined;

  useEffect(() => {
    if (!fieldValue) {
      setValue([]);
    }
  }, [fieldValue]);

  const handleDelete = (idx: number) => {
    const newValue = [...fieldValue];
    newValue.splice(idx, 1);
    setValue(newValue);
    setError(undefined);
  };

  return render(
    <div ref={ref} className={css.field}>
      {!maxCount || fieldValue.length < maxCount ? (
        <label
          htmlFor={name}
          className={`${css.label} ${maxCount && fieldValue.length >= maxCount ? css.disabled : ''}`}
        >
          {label}
          <DocUploadIcon />
        </label>
      ) : null}
      <input
        className={css.input}
        id={name}
        type='file'
        multiple
        accept={accept}
        onChange={(e) => setValue([...fieldValue, ...Array.from(e.target.files || [])])}
      />
      {fieldValue.map((file, idx) => {
        const { name, size, type } = file;
        const oversized = maxSizeValue && size >= maxSizeValue;
        const isImage = withPreview && type.startsWith('image/');
        const previewUrl = '__existing' in file ? (file as unknown as PseudoFile).url : URL.createObjectURL(file);

        if (isImage)
          return (
            <div className={`${css.preview} ${oversized ? css.inputError : ''}`} key={idx}>
              <img
                src={previewUrl}
                className={css.image}
                onLoad={(e) => URL.revokeObjectURL((e.target as HTMLImageElement).src)}
              />
              {oversized && <span className={css.fileOversized}>oversized</span>}
              <CloseIcon className={css.imageDelete} title='Delete file' onClick={() => handleDelete(idx)} />
            </div>
          );

        return (
          <div key={idx} className={`${css.file} ${oversized ? css.inputError : ''}`}>
            <div className={css.text}>{name}</div>
            {oversized && <span className={css.fileOversized}>oversized</span>}
            <CloseIcon className={css.fileDelete} title='Delete file' onClick={() => handleDelete(idx)} />
          </div>
        );
      })}
      {showError ? <div className={css.error}>{error}</div> : null}
    </div>
  );
};

export default UploadField;
